import { z } from 'zod';

/**
 * The home plan: which personalizable sections the wayfinder home shows, in what
 * order, and which catalogue items fill them.
 *
 * The contract lives here rather than beside the composer because the `home_plans`
 * table types its `plan` column from it (ADR-066 Rule 2), and `db/schema.ts` cannot
 * import a package that already depends on this one. The composer owns the *default*
 * plan and the skeleton it walks; this module owns only the shape.
 *
 * Two properties are load-bearing and easy to erode:
 *
 * 1. **No copy.** There is no string-content field anywhere below. A plan names
 *    sections and items; the strings they render stay in the locale catalog
 *    (ADR-017), so a plan can never become a second source of member-facing copy
 *    outside the catalog's key lifecycle and deprecation manifest.
 * 2. **The vocabulary is the shell/slot boundary.** `HOME_PLAN_SECTION_KEYS` lists
 *    only sections a plan may govern. Sections that must not vary — anything with an
 *    intraday-correctness or safety requirement — are absent, so naming one is a
 *    parse failure rather than something a downstream guard has to catch.
 */

/**
 * How a plan row was produced.
 *
 * `rules` and `llm` have no producer yet; they are in the vocabulary so the
 * producers that arrive later need no migration to describe themselves.
 */
export const HOME_PLAN_SOURCE = ['static', 'rules', 'llm'] as const;
export const homePlanSourceSchema = z.enum(HOME_PLAN_SOURCE);
export type HomePlanSource = z.infer<typeof homePlanSourceSchema>;

/**
 * The sections a plan may order, omit, or parameterize.
 *
 * Deliberately excludes every pinned section. The pinned set and the invariants
 * behind it live with the composer (`@galapagos/wayfinder-surface`), which also
 * carries the drift guard asserting these two sets stay disjoint and together cover
 * every section the author can place.
 */
export const HOME_PLAN_SECTION_KEYS = ['looking-ahead', 'my-progress', 'ask-sona'] as const;
export const homePlanSectionKeySchema = z.enum(HOME_PLAN_SECTION_KEYS);
export type HomePlanSectionKey = z.infer<typeof homePlanSectionKeySchema>;

/**
 * One planned section.
 *
 * `itemIds` narrows and orders the section's catalogue items — ids only, never the
 * items' copy. It is general rather than per-section because "rank these defined
 * items" is the operation a later producer performs, and giving each section its own
 * field shape would make that a contract change instead of new data.
 */
/**
 * A catalogue item id.
 *
 * An item id must name a catalogue entry and carry no free text, but the catalogues
 * live in `@galapagos/wayfinder-surface`, which `shared` must not import (ADR-001) —
 * so what this schema can enforce is a bound and a shape, not membership: a lowercase
 * slug of at most 64 characters.
 *
 * That is a narrowing, not a closure, and it is worth being exact about what it does
 * not achieve. A slug can still carry meaning a human put there (`marcus-has-cancer`
 * parses), so the schema is not what keeps PHI out of this column — the writer is.
 * Today the only writer is the seed migration; when the producer lands, the closed
 * vocabulary has to be enforced where the catalogue is actually in scope (the
 * producer, or a read-time check in `wayfinder-surface`), because it cannot be
 * enforced here. What the bound does buy is that no stored row can make the
 * first-screen parse unbounded, and that ids cannot hold prose with spaces or
 * punctuation. An id outside the real catalogue stays shape-valid and is dropped by
 * the composer, which is the intended fail-soft.
 */
const homePlanItemIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be a lowercase slug');

export const homePlanSectionSchema = z
  .object({
    key: homePlanSectionKeySchema,
    // Bounded so a single stored row cannot make the first screen's work unbounded;
    // the largest real catalogue is six items, so 32 is slack, not a limit anyone
    // legitimately reaches.
    itemIds: z.array(homePlanItemIdSchema).max(32).optional(),
  })
  .strict();
export type HomePlanSection = z.infer<typeof homePlanSectionSchema>;

/**
 * A whole plan.
 *
 * Duplicate keys are rejected rather than deduped: each section key becomes an
 * element key in the composed document, so a duplicate would collide there. Silently
 * collapsing it would hide a producer bug behind a plausible-looking home.
 */
export const homePlanSchema = z
  .object({
    // Bounded for the same reason `itemIds` is, and the bound is exact rather than
    // generous: keys are unique and the vocabulary is closed, so no valid plan can
    // carry more entries than there are keys. Without it `superRefine` below walks
    // every element of an arbitrarily long array before the duplicate-key rule
    // rejects it, on the request path for the app's first screen.
    sections: z.array(homePlanSectionSchema).max(HOME_PLAN_SECTION_KEYS.length),
  })
  .strict()
  .superRefine((plan, ctx) => {
    const seen = new Set<string>();
    plan.sections.forEach((section, index) => {
      if (seen.has(section.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sections', index, 'key'],
          message: `duplicate section key '${section.key}'`,
        });
      }
      seen.add(section.key);

      // Same hazard one level down, and for the same reason: an item id becomes an
      // element key in the composed document, so a repeat produces two `children`
      // entries pointing at one element. Graph validation only checks that each
      // child exists, so such a document is served and the row renders twice under
      // one React key. Rejecting the section key but admitting the item id would
      // leave the next reader assuming this was covered.
      const items = section.itemIds;
      if (!items) return;
      const seenItems = new Set<string>();
      items.forEach((id, itemIndex) => {
        if (seenItems.has(id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sections', index, 'itemIds', itemIndex],
            message: `duplicate item id '${id}'`,
          });
        }
        seenItems.add(id);
      });
    });
  });
export type HomePlan = z.infer<typeof homePlanSchema>;

/**
 * The plan contract's version, stored on every row.
 *
 * Rows are read only at the version the code currently speaks, so revising the
 * section vocabulary means a new version plus a new seed — never a migration of
 * stored plans. That is what keeps the vocabulary cheap to change while a redesign
 * is still in flight.
 *
 * Distinct from the `SurfaceDocument` envelope's own `schema_version`, which versions
 * the rendered document rather than the plan that shaped it.
 */
export const HOME_PLAN_SCHEMA_VERSION = 2;
